import throttle from "lodash/throttle";
// React
import { useEffect } from "react";
// Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Constants
import { SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { UndoRedoThrottleIntervalMs } from "@ms/uno-constants/lib/local/ShortcutConstants";
// Routing
import { extractPlanRoutingDetailsFromSubRoute } from "@ms/uno-routing";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Utilities
import { resolveUndoOperationScopeId } from "@ms/planner-logicmodules/lib/local/utilities/UndoRedoUtilities";
import { isOsMac } from "@ms/uno-utilities/lib/local/BrowserUtilities";
/**
 * Registers document-level keyboard shortcuts for undo (Ctrl+Z / Cmd+Z) and redo (Ctrl+Y / Cmd+Shift+Z).
 * Handles platform differences automatically (Windows/Linux vs Mac).
 * Only fires when the current view is a basic plan; passes the current view id as the operation scope id.
 *
 * The listener is registered in the capture phase so the shortcut still reaches this handler even when
 * focus is inside a subtree that stops keydown propagation (e.g. a board bucket column, whose root
 * `onKeyDown` unconditionally calls stopPropagation).
 * @param acLoader Loader for action creators
 * @param storeLoader Loader for stores
 * @param configProvider Provider for app configuration (used to check the EnableUndoRedoInEvoPlans flight)
 */ export function useUndoRedoKeyboardHandler(acLoader, storeLoader, configProvider) {
    useEffect(()=>{
        if (!configProvider().flights.EnableUndoRedoInEvoPlans) {
            return;
        }
        const isMac = isOsMac();
        const handleValidShortcut = throttle((handleShortcut)=>{
            handleShortcut();
        }, UndoRedoThrottleIntervalMs, {
            leading: true,
            trailing: false
        });
        const onKeyDown = (event)=>{
            const routeStore = storeLoader.load(Store.Route);
            const subRouteType = routeStore.getCurrentSubRouteType();
            if (subRouteType !== SubRouteType.BasicPlan) {
                return;
            }
            const planRoutingDetails = extractPlanRoutingDetailsFromSubRoute(routeStore.getCurrentSubRoute(), subRouteType);
            const planId = planRoutingDetails?.planId;
            if (!planId) {
                return;
            }
            const key = event.key.toLowerCase();
            const isControlKeyPressed = event.ctrlKey;
            const isCommandKeyPressed = event.metaKey;
            if (!isControlKeyPressed && !isCommandKeyPressed || event.altKey || key !== "z" && key !== "y") {
                return;
            }
            const operationScopeId = resolveUndoOperationScopeId(storeLoader);
            const undoRedoAC = acLoader.load(ActionCreator.UndoRedo);
            // Undo: Ctrl+Z (Windows/Linux) or Cmd+Z (Mac)
            if (key === "z" && !event.shiftKey) {
                event.preventDefault();
                handleValidShortcut(()=>{
                    undoRedoAC.undo(operationScopeId);
                });
            } else if (!isMac && key === "y" || isMac && key === "z" && event.shiftKey) {
                event.preventDefault();
                handleValidShortcut(()=>{
                    undoRedoAC.redo(operationScopeId);
                });
            }
        };
        // The third argument (useCapture=true) registers the listener in the capture phase so it runs
        // before descendant handlers. This ensures the undo/redo shortcut is handled even when a child
        // element calls stopPropagation() on the keydown event during the bubbling phase.
        document.addEventListener("keydown", onKeyDown, true);
        return ()=>{
            document.removeEventListener("keydown", onKeyDown, true);
            handleValidShortcut.cancel();
        };
    }, [
        acLoader,
        storeLoader,
        configProvider
    ]);
}
